import Carousel from "react-slick";
import { useState, useRef, useEffect } from "react";
import { styled } from "@material-ui/core/styles";
import { Box, Stack, Typography } from "@material-ui/core";
import { Image } from "../Image";
import { CarouselArrowIndex } from "./CarouselArrowIndex";
import LightboxModal from "components/LightboxModal";
import { ImageNotSupported } from "@material-ui/icons";
import { useModal } from "hooks/useModal";
import { getFilteredPictures } from "./CarouselCard";
import type { Listing } from "../../@types/listings/listing";
import type { CropToImageProps } from "../Image";

const THUMB_SIZE = 64;

type CarouselThumbnailProps = Pick<Listing, "pictures"> &
  CropToImageProps & {
    imagesHidden?: boolean;
    hiddenPictures?: string[];
    inPublicListingPage?: boolean;
    filterHiddenPictures?: boolean;
  };

type StyledThumbnailsContainerProps = {
  length: number;
};

const StyledThumbnailsContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "length"
})<StyledThumbnailsContainerProps>(({ length, theme }) => ({
  margin: theme.spacing(0, "auto"),
  position: "relative",
  "& .slick-slide": {
    opacity: 0.48,
    "&.slick-current": {
      opacity: 1
    },
    "& > div": {
      padding: theme.spacing(0, 0.75)
    }
  },
  ...(length === 1 && {
    maxWidth: THUMB_SIZE * 1 + 16
  }),
  ...(length === 2 && {
    maxWidth: THUMB_SIZE * 2 + 32
  }),
  ...((length === 3 || length === 4) && {
    maxWidth: THUMB_SIZE * 3 + 48
  }),
  ...(length >= 5 && {
    maxWidth: THUMB_SIZE * 6
  })
}));

export function CarouselThumbnail({
  pictures,
  imagesHidden,
  cropTopImage,
  hiddenPictures,
  inPublicListingPage,
  filterHiddenPictures
}: CarouselThumbnailProps): JSX.Element {
  const { open, openModal, closeModal } = useModal();

  const [modalIndex, setModalIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const [mainCarousel, setMainCarousel] = useState<Carousel | undefined>(undefined);

  const [thumbnailCarousel, setThumbnailCarousel] = useState<Carousel | undefined>(undefined);

  const mainCarouselRef = useRef<Carousel | null>(null);

  const thumbnailCarouselRef = useRef<Carousel | null>(null);

  useEffect(() => {
    if (mainCarouselRef.current) setMainCarousel(mainCarouselRef.current);
    if (thumbnailCarouselRef.current) setThumbnailCarousel(thumbnailCarouselRef.current);
  }, []);

  const handlePrev = (): void => thumbnailCarouselRef.current?.slickPrev();

  const handleNext = (): void => thumbnailCarouselRef.current?.slickNext();

  const handleOpenModal = (index: number) => (): void => {
    setModalIndex(index);
    openModal();
  };

  const filteredPictures = getFilteredPictures(pictures, filterHiddenPictures, hiddenPictures);

  const fixedImageHeight = inPublicListingPage ? 384 : 300;

  const showPictures = !imagesHidden && filteredPictures?.length;

  if (!showPictures)
    return (
      <Stack
        sx={{ height: fixedImageHeight, backgroundColor: "background.neutral", borderRadius: 1 }}
        justifyContent="center"
        alignItems="center"
      >
        {imagesHidden ? (
          <ImageNotSupported
            sx={{
              width: 64,
              height: 64
            }}
            color="disabled"
          />
        ) : (
          <Typography>No images</Typography>
        )}
      </Stack>
    );

  const carouselSettings1 = {
    dots: false,
    arrows: false,
    slidesToShow: 1,
    draggable: true,
    slidesToScroll: 1,
    adaptiveHeight: false,
    beforeChange: (_current: number, next: number) => setCarouselIndex(next)
  };

  const carouselSettings2 = {
    dots: true,
    arrows: false,
    centerMode: true,
    swipeToSlide: true,
    focusOnSelect: true,
    variableWidth: true,
    centerPadding: "0px",
    slidesToShow: filteredPictures?.length > 3 ? 3 : filteredPictures?.length
  };

  const images = filteredPictures.map(({ src }) => src);

  return (
    <Box
      sx={{
        "& .slick-slide": {
          float: (theme) => (theme.direction === "rtl" ? "right" : "left")
        }
      }}
    >
      <LightboxModal
        isOpen={open}
        images={images}
        photoIndex={modalIndex}
        cropTopImage={cropTopImage}
        onClose={closeModal}
        setPhotoIndex={setModalIndex}
      />
      <Box
        sx={{
          mb: 3,
          zIndex: 0,
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          maxWidth: "1072px"
        }}
      >
        <Carousel {...carouselSettings1} asNavFor={thumbnailCarousel} ref={mainCarouselRef}>
          {filteredPictures.map(({ id, src, alt }, index) => (
            <Image
              sx={{
                height: fixedImageHeight
              }}
              src={src}
              alt={alt}
              cropTopImage={cropTopImage}
              onClick={handleOpenModal(index)}
              key={id}
            />
          ))}
        </Carousel>
        <CarouselArrowIndex
          index={carouselIndex}
          total={filteredPictures.length}
          onNext={handleNext}
          onPrevious={handlePrev}
        />
      </Box>
      <StyledThumbnailsContainer length={filteredPictures.length}>
        <Carousel {...carouselSettings2} asNavFor={mainCarousel} ref={thumbnailCarouselRef}>
          {filteredPictures.map(({ id, src, alt }, index) => (
            <Image
              src={src}
              alt={alt}
              sx={{
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                borderRadius: 1.5,
                cursor: "pointer",
                ...(carouselIndex === index && {
                  border: (theme) => `solid 2px ${theme.palette.primary.main}`
                })
              }}
              key={id}
            />
          ))}
        </Carousel>
      </StyledThumbnailsContainer>
    </Box>
  );
}
